<script setup lang="ts">
import { computed } from 'vue';

import type {
  GlobalSearchApplicationRow,
  GlobalSearchCandidateGroup,
} from '@/core/conversations/conversation-index/conversation-index.type';
import { splitTextForHighlight } from '@/utils/text-highlight.util';
import { isPhoneLikeQuery } from '@/core/conversations/conversation-index/global-search.util';

import GlobalCandidateSearchRow from './GlobalCandidateSearchRow.vue';

const props = defineProps<{
  group: GlobalSearchCandidateGroup;
  query: string;
}>();

const emit = defineEmits<{
  (e: 'select', payload: GlobalSearchApplicationRow): void;
}>();

// Derived from the candidate's pre-cap total, not the (possibly truncated)
// rendered rows, so a candidate cut down to one row keeps their group header.
const isSingle = computed(() => props.group.totalRows === 1);

/**
 * Says "2 of 5 applications" when the row cap truncated this candidate's
 * group — the truncated length alone would misstate the candidate's total.
 */
const applicationsCountLabel = computed(() => {
  const shown = props.group.rows.length;
  const total = props.group.totalRows;
  return total > shown ? `${shown} of ${total} applications` : `${total} applications`;
});

const nameParts = computed(() => {
  if (isPhoneLikeQuery(props.query)) {
    return [{ text: props.group.conversationIndex.proName, isMatch: false }];
  }
  return splitTextForHighlight(props.group.conversationIndex.proName, props.query);
});
</script>

<!-- The group header is a label, not a click target; "Last active" is
     deliberately absent because it can contradict the per-application rail. -->
<template>
  <GlobalCandidateSearchRow
    v-if="isSingle"
    :row="group.rows[0]"
    :query="query"
    show-candidate-name
    @select="emit('select', $event)"
  />

  <div v-else class="py-2" data-test="global-search-group">
    <div class="px-4 pb-1">
      <span class="sensitive block text-sm font-semibold text-shade-880">
        <template v-for="(part, index) in nameParts" :key="index">
          <mark v-if="part.isMatch" class="rounded-[3px] bg-highlight-100 text-shade-880">{{
            part.text
          }}</mark>
          <template v-else>{{ part.text }}</template>
        </template>
      </span>
      <span class="mt-1 block text-xs text-shade-700" data-test="global-search-group-count">{{
        applicationsCountLabel
      }}</span>
    </div>
    <div class="ml-4 space-y-0.5 border-l-2 border-tint-60 pl-2">
      <GlobalCandidateSearchRow
        v-for="row in group.rows"
        :key="row.application.applicationId"
        :row="row"
        :query="query"
        @select="emit('select', $event)"
      />
    </div>
  </div>
</template>
